import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, ArrowLeft, Check, Loader2, User, Mail, Phone, MapPin, Building, Briefcase, Globe, Calendar } from 'lucide-react';
import axios from 'axios';
import LanguageSelector from '../components/LanguageSelector';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_sgficapital/artifacts/i5d6sap9_LOGO%20SGFI-nuevo.png';

// Step indicator component
const StepIndicator = ({ currentStep, steps }) => {
  return (
    <div className="flex items-center justify-center gap-1 md:gap-2 mb-8 overflow-x-auto pb-2 px-2">
      {steps.map((step, index) => (
        <div key={index} className="flex items-center">
          <div className="flex flex-col items-center min-w-[60px] md:min-w-[100px]">
            <div className={`h-1 w-full mb-2 rounded-full transition-all ${
              index <= currentStep ? 'bg-blue-500' : 'bg-white/10'
            }`} />
            <span className={`text-[10px] md:text-xs uppercase tracking-wider text-center ${
              index <= currentStep ? 'text-white' : 'text-[#93a5af]/50'
            }`}>
              <span className="font-semibold">{String(index + 1).padStart(2, '0')}</span>
              <span className="hidden md:inline ml-1">— {step}</span>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

// Radio option component
const RadioOption = ({ name, value, label, selected, onChange, className = '' }) => (
  <label 
    className={`flex items-center p-4 bg-[#1c262d] border border-white/10 rounded-xl cursor-pointer transition-all hover:border-white/20 hover:bg-[#252F38] ${
      selected ? 'border-blue-500/50 bg-blue-500/5' : ''
    } ${className}`}
  >
    <div className={`w-5 h-5 rounded-full border-2 mr-4 flex items-center justify-center flex-shrink-0 transition-all ${
      selected ? 'border-blue-500 bg-blue-500' : 'border-[#93a5af]/50'
    }`}>
      {selected && <div className="w-2 h-2 rounded-full bg-white" />}
    </div>
    <span className="text-white/90 text-sm">{label}</span>
    <input
      type="radio"
      name={name}
      value={value}
      checked={selected}
      onChange={onChange}
      className="sr-only"
    />
  </label>
);

// Multi-select tag component
const TagSelect = ({ options, selected, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(option => (
      <button
        key={option.value}
        type="button"
        onClick={() => {
          if (selected.includes(option.value)) {
            onChange(selected.filter(s => s !== option.value));
          } else {
            onChange([...selected, option.value]);
          }
        }}
        className={`px-4 py-2 rounded-full text-sm transition-all ${
          selected.includes(option.value)
            ? 'bg-blue-500 text-white'
            : 'bg-[#1c262d] border border-white/10 text-[#93a5af] hover:border-white/20 hover:text-white'
        }`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

// Scale selector component (1-10)
const ScaleSelector = ({ value, onChange, leftLabel, rightLabel }) => (
  <div>
    <div className="flex justify-between gap-1 md:gap-2 mb-3">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
        <button
          key={num}
          type="button"
          onClick={() => onChange(num)}
          className={`flex-1 h-10 md:h-12 rounded-lg text-sm font-medium transition-all ${
            value === num
              ? 'bg-blue-500 text-white'
              : 'bg-[#1c262d] border border-white/10 text-[#93a5af] hover:border-white/20 hover:text-white'
          }`}
        >
          {num}
        </button>
      ))}
    </div>
    <div className="flex justify-between text-xs text-[#93a5af] uppercase tracking-wider">
      <span>{leftLabel}</span>
      <span>{rightLabel}</span>
    </div>
  </div>
);

// Input field component
const FormInput = ({ label, type = 'text', value, onChange, placeholder, required = false, icon: Icon, optionalText }) => (
  <div>
    <label className="block text-xs uppercase tracking-wider text-[#93a5af] mb-2">
      {label} {!required && optionalText && <span className="text-[#93a5af]/50">— {optionalText}</span>}
    </label>
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#93a5af]/50" />
      )}
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className={`w-full bg-[#1c262d] border border-white/10 rounded-xl py-3 text-white placeholder-[#93a5af]/50 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 transition-all ${
          Icon ? 'pl-12 pr-4' : 'px-4'
        }`}
      />
    </div>
  </div>
);

// Textarea component
const FormTextarea = ({ label, value, onChange, placeholder, rows = 4 }) => (
  <div>
    <label className="block text-xs uppercase tracking-wider text-[#93a5af] mb-2">
      {label}
    </label>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className="w-full bg-[#1c262d] border border-white/10 rounded-xl p-4 text-white placeholder-[#93a5af]/50 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 transition-all resize-none"
    />
  </div>
);

export default function ClientQualification() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [currentStep, setCurrentStep] = useState(-1); // -1 = hero, 0-4 = steps
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    // Step 1 - Profile
    fullName: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    age: '',
    profession: '',
    company: '',
    socialLinks: '',
    
    // Step 2 - Investment Capacity
    initialAmount: '',
    futureAmount: '',
    capitalOrigin: '',
    investmentExperience: '',
    investmentAreas: [],
    
    // Step 3 - Objectives
    mainMotivation: '',
    timeHorizon: '',
    readyToAdvance: '',
    whySGFI: '',
    meetingExpectations: '',
    
    // Step 4 - Strategic Value
    networkAccess: '',
    sectorExperience: [],
    contribution: '',
    idealResult: '',
    
    // Step 5 - Final Assessment
    interestLevel: null,
    probabilityToAdvance: null,
    restrictions: '',
    confirmData: false
  });

  const steps = [
    t('qualification.steps.profile'),
    t('qualification.steps.investment'),
    t('qualification.steps.objectives'),
    t('qualification.steps.value'),
    t('qualification.steps.assessment')
  ];

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    if (!formData.confirmData) {
      setError(t('qualification.step5.errorConfirm'));
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      // Include current language in the submission
      const dataWithLang = {
        ...formData,
        language: i18n.language || 'pt'
      };
      await axios.post(`${API}/api/client-qualification`, dataWithLang);
      setIsSubmitted(true);
    } catch (err) {
      setError(t('qualification.step5.errorSubmit'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Amount options for Step 2
  const initialAmounts = [
    { value: '€1.000 - €5.000', label: t('qualification.step2.amounts.1k5k') },
    { value: '€5.000 - €10.000', label: t('qualification.step2.amounts.5k10k') },
    { value: '€10.000 - €25.000', label: t('qualification.step2.amounts.10k25k') },
    { value: '€25.000 - €50.000', label: t('qualification.step2.amounts.25k50k') },
    { value: '€50.000 - €100.000', label: t('qualification.step2.amounts.50k100k') },
    { value: '€100.000 - €250.000', label: t('qualification.step2.amounts.100k250k') },
    { value: '€250.000 - €500.000', label: t('qualification.step2.amounts.250k500k') },
    { value: 'Mais de €500.000', label: t('qualification.step2.amounts.500kPlus') }
  ];

  const futureAmounts = [
    { value: 'Até €10.000', label: t('qualification.step2.futureAmounts.to10k') },
    { value: '€10.000 - €50.000', label: t('qualification.step2.futureAmounts.10k50k') },
    { value: '€50.000 - €100.000', label: t('qualification.step2.futureAmounts.50k100k') },
    { value: '€100.000 - €250.000', label: t('qualification.step2.futureAmounts.100k250k') },
    { value: 'Mais de €250.000', label: t('qualification.step2.futureAmounts.250kPlus') },
    { value: 'Ainda não definido', label: t('qualification.step2.futureAmounts.notDefined') }
  ];

  const capitalOrigins = [
    { value: 'Rendimentos profissionais', label: t('qualification.step2.origins.professional') },
    { value: 'Lucros empresariais', label: t('qualification.step2.origins.business') },
    { value: 'Poupanças', label: t('qualification.step2.origins.savings') },
    { value: 'Venda de ativos', label: t('qualification.step2.origins.assetSale') },
    { value: 'Rendimentos de investimentos', label: t('qualification.step2.origins.investments') },
    { value: 'Outro', label: t('qualification.step2.origins.other') }
  ];

  const experienceOptions = [
    { value: 'Nenhuma', label: t('qualification.step2.experienceOptions.none') },
    { value: 'Menos de 1 ano', label: t('qualification.step2.experienceOptions.lessThan1') },
    { value: '1-3 anos', label: t('qualification.step2.experienceOptions.1to3') },
    { value: '3-5 anos', label: t('qualification.step2.experienceOptions.3to5') },
    { value: 'Mais de 5 anos', label: t('qualification.step2.experienceOptions.moreThan5') }
  ];

  const investmentAreas = [
    { value: 'Mercados financeiros', label: t('qualification.step2.investmentAreas.financial') },
    { value: 'Forex', label: t('qualification.step2.investmentAreas.forex') },
    { value: 'Ouro / Commodities', label: t('qualification.step2.investmentAreas.commodities') },
    { value: 'Ações / ETFs', label: t('qualification.step2.investmentAreas.stocks') },
    { value: 'Criptoativos', label: t('qualification.step2.investmentAreas.crypto') },
    { value: 'Imobiliário', label: t('qualification.step2.investmentAreas.realEstate') },
    { value: 'Private Equity', label: t('qualification.step2.investmentAreas.privateEquity') },
    { value: 'Outros', label: t('qualification.step2.investmentAreas.other') }
  ];

  // Step 3 options
  const motivations = [
    { value: 'Conhecer melhor a SGFI', label: t('qualification.step3.motivations.knowSGFI') },
    { value: 'Conhecer oportunidades de investimento', label: t('qualification.step3.motivations.opportunities') },
    { value: 'Diversificar o meu património', label: t('qualification.step3.motivations.diversify') },
    { value: 'Avaliar uma oportunidade de investimento de maior dimensão', label: t('qualification.step3.motivations.largeInvestment') },
    { value: 'Construir uma relação estratégica de longo prazo', label: t('qualification.step3.motivations.longTerm') },
    { value: 'Avaliar uma possível parceria empresarial', label: t('qualification.step3.motivations.partnership') },
    { value: 'Outro', label: t('qualification.step3.motivations.other') }
  ];

  const timeHorizons = [
    { value: 'Estou apenas a explorar', label: t('qualification.step3.horizons.exploring') },
    { value: '6-12 meses', label: t('qualification.step3.horizons.6to12months') },
    { value: '3-6 meses', label: t('qualification.step3.horizons.3to6months') },
    { value: '1-3 meses', label: t('qualification.step3.horizons.1to3months') },
    { value: 'Estou preparado para avaliar de imediato', label: t('qualification.step3.horizons.immediately') }
  ];

  const readyOptions = [
    { value: 'Não', label: t('qualification.step3.readyOptions.no') },
    { value: 'Preciso de analisar primeiro', label: t('qualification.step3.readyOptions.needAnalysis') },
    { value: 'Provavelmente', label: t('qualification.step3.readyOptions.probably') },
    { value: 'Sim', label: t('qualification.step3.readyOptions.yes') }
  ];

  // Step 4 options
  const networkOptions = [
    { value: 'Não', label: t('qualification.step4.networkOptions.no') },
    { value: 'Algumas pessoas', label: t('qualification.step4.networkOptions.somePeople') },
    { value: 'Uma rede relevante', label: t('qualification.step4.networkOptions.relevantNetwork') },
    { value: 'Tenho acesso direto a vários investidores / empresários', label: t('qualification.step4.networkOptions.directAccess') }
  ];

  const sectorOptions = [
    { value: 'Investimento / Finanças', label: t('qualification.step4.sectors.finance') },
    { value: 'Imobiliário', label: t('qualification.step4.sectors.realEstate') },
    { value: 'Empresariado', label: t('qualification.step4.sectors.business') },
    { value: 'Banca / Seguros', label: t('qualification.step4.sectors.banking') },
    { value: 'Tecnologia', label: t('qualification.step4.sectors.technology') },
    { value: 'Internacionalização', label: t('qualification.step4.sectors.international') },
    { value: 'Private Equity', label: t('qualification.step4.sectors.privateEquity') },
    { value: 'Outro', label: t('qualification.step4.sectors.other') }
  ];

  // Hero Screen
  if (currentStep === -1) {
    return (
      <div className="min-h-screen bg-[#0d1218] relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-blue-500/10 rounded-full blur-3xl" />
        
        {/* Content */}
        <div className="relative z-10 min-h-screen flex flex-col">
          {/* Header */}
          <header className="p-6 md:p-8">
            <div className="max-w-6xl mx-auto flex items-center justify-between">
              <img src={LOGO_URL} alt="SGFI Capital" className="h-10 md:h-12" />
              <div className="flex items-center gap-4">
                <LanguageSelector />
                <button
                  onClick={() => navigate('/login')}
                  className="text-[#93a5af] hover:text-white text-sm transition-colors hidden md:block"
                >
                  {t('qualification.hero.hasAccount')}
                </button>
              </div>
            </div>
          </header>
          
          {/* Main Content */}
          <main className="flex-1 flex items-center justify-center px-6 py-12">
            <div className="text-center max-w-3xl">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-8">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                <span className="text-blue-400 text-sm font-medium">{t('qualification.hero.badge')}</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {t('qualification.hero.title')}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600"> {t('qualification.hero.titleHighlight')}</span>
              </h1>
              
              <p className="text-[#93a5af] text-lg md:text-xl mb-8 max-w-2xl mx-auto leading-relaxed">
                {t('qualification.hero.description')}
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <div className="flex items-center gap-2 text-[#93a5af]">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-sm">{t('qualification.hero.confidential')}</span>
                </div>
                <div className="flex items-center gap-2 text-[#93a5af]">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-sm">{t('qualification.hero.duration')}</span>
                </div>
                <div className="flex items-center gap-2 text-[#93a5af]">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-sm">{t('qualification.hero.noCommitment')}</span>
                </div>
              </div>
              
              <button
                onClick={() => setCurrentStep(0)}
                data-testid="start-qualification-btn"
                className="inline-flex items-center px-8 py-4 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-all hover:scale-105 shadow-lg shadow-blue-500/25"
              >
                {t('qualification.hero.startButton')}
                <ChevronRight className="ml-2 w-5 h-5" />
              </button>
            </div>
          </main>
          
          {/* Footer */}
          <footer className="p-6 text-center">
            <p className="text-[#93a5af]/50 text-sm">
              {t('qualification.footer')}
            </p>
          </footer>
        </div>
      </div>
    );
  }

  // Success Screen
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#0d1218] flex items-center justify-center px-6">
        <div className="text-center max-w-lg">
          <div className="w-20 h-20 bg-green-500/20 border border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-8">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('qualification.success.title')}
          </h1>
          <p className="text-[#93a5af] text-lg mb-8">
            {t('qualification.success.message')}
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center px-6 py-3 bg-[#1c262d] border border-white/10 text-white font-medium rounded-xl hover:bg-[#252F38] transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('qualification.success.backHome')}
          </button>
        </div>
      </div>
    );
  }

  // Form Steps
  return (
    <div className="min-h-screen bg-[#0d1218]">
      {/* Header */}
      <header className="bg-[#1c262d] border-b border-white/5 p-4 md:p-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img src={LOGO_URL} alt="SGFI Capital" className="h-8 md:h-10" />
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <span className="text-[#93a5af] text-sm hidden md:block">
              {t('qualification.navigation.step')} {currentStep + 1} {t('qualification.navigation.of')} 5
            </span>
          </div>
        </div>
      </header>
      
      {/* Progress Bar */}
      <div className="bg-[#1c262d] pt-4 pb-6">
        <div className="max-w-4xl mx-auto px-4">
          <StepIndicator currentStep={currentStep} steps={steps} />
        </div>
      </div>
      
      {/* Form Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Step 1: Profile */}
        {currentStep === 0 && (
          <div className="space-y-8">
            <div>
              <span className="text-blue-400 text-sm font-medium uppercase tracking-wider">{t('qualification.step1.label')}</span>
              <h2 className="text-2xl md:text-3xl font-bold text-white mt-2">
                {t('qualification.step1.title')}
              </h2>
              <p className="text-[#93a5af] mt-2">{t('qualification.step1.subtitle')}</p>
            </div>
            
            <div className="space-y-5">
              <FormInput
                label={t('qualification.step1.fullName')}
                value={formData.fullName}
                onChange={(e) => updateField('fullName', e.target.value)}
                required
                icon={User}
                placeholder={t('qualification.step1.fullNamePlaceholder')}
              />
              
              <div className="grid md:grid-cols-2 gap-5">
                <FormInput
                  label={t('qualification.step1.email')}
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  required
                  icon={Mail}
                  placeholder={t('qualification.step1.emailPlaceholder')}
                />
                <FormInput
                  label={t('qualification.step1.phone')}
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  required
                  icon={Phone}
                  placeholder={t('qualification.step1.phonePlaceholder')}
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-5">
                <FormInput
                  label={t('qualification.step1.country')}
                  value={formData.country}
                  onChange={(e) => updateField('country', e.target.value)}
                  required
                  icon={Globe}
                  placeholder={t('qualification.step1.countryPlaceholder')}
                />
                <FormInput
                  label={t('qualification.step1.city')}
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  required
                  icon={MapPin}
                  placeholder={t('qualification.step1.cityPlaceholder')}
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-5">
                <FormInput
                  label={t('qualification.step1.age')}
                  type="number"
                  value={formData.age}
                  onChange={(e) => updateField('age', e.target.value)}
                  required
                  icon={Calendar}
                  placeholder={t('qualification.step1.agePlaceholder')}
                />
                <FormInput
                  label={t('qualification.step1.profession')}
                  value={formData.profession}
                  onChange={(e) => updateField('profession', e.target.value)}
                  required
                  icon={Briefcase}
                  placeholder={t('qualification.step1.professionPlaceholder')}
                />
              </div>
              
              <FormInput
                label={t('qualification.step1.company')}
                value={formData.company}
                onChange={(e) => updateField('company', e.target.value)}
                icon={Building}
                placeholder={t('qualification.step1.companyPlaceholder')}
                optionalText={t('qualification.step1.optional')}
              />
              
              <FormInput
                label={t('qualification.step1.socialLinks')}
                value={formData.socialLinks}
                onChange={(e) => updateField('socialLinks', e.target.value)}
                required={false}
                icon={Globe}
                placeholder={t('qualification.step1.socialLinksPlaceholder')}
                optionalText={t('qualification.step1.optional')}
              />
            </div>
          </div>
        )}
        
        {/* Step 2: Investment Capacity */}
        {currentStep === 1 && (
          <div className="space-y-10">
            <div>
              <span className="text-blue-400 text-sm font-medium uppercase tracking-wider">{t('qualification.step2.label')}</span>
              <h2 className="text-2xl md:text-3xl font-bold text-white mt-2">
                {t('qualification.step2.title')}
              </h2>
              <p className="text-[#93a5af] mt-2">{t('qualification.step2.subtitle')}</p>
            </div>
            
            {/* Initial Amount */}
            <div>
              <p className="text-white font-medium mb-4">
                {t('qualification.step2.initialAmount')}
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                {initialAmounts.map(amount => (
                  <RadioOption
                    key={amount.value}
                    name="initialAmount"
                    value={amount.value}
                    label={amount.label}
                    selected={formData.initialAmount === amount.value}
                    onChange={() => updateField('initialAmount', amount.value)}
                  />
                ))}
              </div>
            </div>
            
            {/* Future Amount */}
            <div>
              <p className="text-white font-medium mb-4">
                {t('qualification.step2.futureAmount')}
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                {futureAmounts.map(amount => (
                  <RadioOption
                    key={amount.value}
                    name="futureAmount"
                    value={amount.value}
                    label={amount.label}
                    selected={formData.futureAmount === amount.value}
                    onChange={() => updateField('futureAmount', amount.value)}
                  />
                ))}
              </div>
            </div>
            
            {/* Capital Origin */}
            <div>
              <p className="text-white font-medium mb-4">
                {t('qualification.step2.capitalOrigin')}
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                {capitalOrigins.map(origin => (
                  <RadioOption
                    key={origin.value}
                    name="capitalOrigin"
                    value={origin.value}
                    label={origin.label}
                    selected={formData.capitalOrigin === origin.value}
                    onChange={() => updateField('capitalOrigin', origin.value)}
                  />
                ))}
              </div>
            </div>
            
            {/* Investment Experience */}
            <div>
              <p className="text-white font-medium mb-4">
                {t('qualification.step2.experience')}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {experienceOptions.map(exp => (
                  <RadioOption
                    key={exp.value}
                    name="investmentExperience"
                    value={exp.value}
                    label={exp.label}
                    selected={formData.investmentExperience === exp.value}
                    onChange={() => updateField('investmentExperience', exp.value)}
                  />
                ))}
              </div>
            </div>
            
            {/* Investment Areas */}
            <div>
              <p className="text-white font-medium mb-2">
                {t('qualification.step2.areas')}
              </p>
              <p className="text-[#93a5af] text-sm mb-4">{t('qualification.step2.areasHint')}</p>
              <TagSelect
                options={investmentAreas}
                selected={formData.investmentAreas}
                onChange={(areas) => updateField('investmentAreas', areas)}
              />
            </div>
          </div>
        )}
        
        {/* Step 3: Objectives */}
        {currentStep === 2 && (
          <div className="space-y-10">
            <div>
              <span className="text-blue-400 text-sm font-medium uppercase tracking-wider">{t('qualification.step3.label')}</span>
              <h2 className="text-2xl md:text-3xl font-bold text-white mt-2">
                {t('qualification.step3.title')}
              </h2>
              <p className="text-[#93a5af] mt-2">{t('qualification.step3.subtitle')}</p>
            </div>
            
            {/* Main Motivation */}
            <div>
              <p className="text-white font-medium mb-4">
                {t('qualification.step3.motivation')}
              </p>
              <div className="space-y-3">
                {motivations.map(motivation => (
                  <RadioOption
                    key={motivation.value}
                    name="mainMotivation"
                    value={motivation.value}
                    label={motivation.label}
                    selected={formData.mainMotivation === motivation.value}
                    onChange={() => updateField('mainMotivation', motivation.value)}
                  />
                ))}
              </div>
            </div>
            
            {/* Time Horizon */}
            <div>
              <p className="text-white font-medium mb-4">
                {t('qualification.step3.timeHorizon')}
              </p>
              <div className="space-y-3">
                {timeHorizons.map(horizon => (
                  <RadioOption
                    key={horizon.value}
                    name="timeHorizon"
                    value={horizon.value}
                    label={horizon.label}
                    selected={formData.timeHorizon === horizon.value}
                    onChange={() => updateField('timeHorizon', horizon.value)}
                  />
                ))}
              </div>
            </div>
            
            {/* Ready to Advance */}
            <div>
              <p className="text-white font-medium mb-4">
                {t('qualification.step3.readyToAdvance')}
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                {readyOptions.map(ready => (
                  <RadioOption
                    key={ready.value}
                    name="readyToAdvance"
                    value={ready.value}
                    label={ready.label}
                    selected={formData.readyToAdvance === ready.value}
                    onChange={() => updateField('readyToAdvance', ready.value)}
                  />
                ))}
              </div>
            </div>
            
            {/* Why SGFI */}
            <FormTextarea
              label={t('qualification.step3.whySGFI')}
              value={formData.whySGFI}
              onChange={(e) => updateField('whySGFI', e.target.value)}
              placeholder={t('qualification.step3.whySGFIPlaceholder')}
            />
            
            {/* Meeting Expectations */}
            <FormTextarea
              label={t('qualification.step3.meetingExpectations')}
              value={formData.meetingExpectations}
              onChange={(e) => updateField('meetingExpectations', e.target.value)}
              placeholder={t('qualification.step3.meetingExpectationsPlaceholder')}
            />
          </div>
        )}
        
        {/* Step 4: Strategic Value */}
        {currentStep === 3 && (
          <div className="space-y-10">
            <div>
              <span className="text-blue-400 text-sm font-medium uppercase tracking-wider">{t('qualification.step4.label')}</span>
              <h2 className="text-2xl md:text-3xl font-bold text-white mt-2">
                {t('qualification.step4.title')}
              </h2>
              <p className="text-[#93a5af] mt-2">{t('qualification.step4.subtitle')}</p>
            </div>
            
            {/* Network Access */}
            <div>
              <p className="text-white font-medium mb-4">
                {t('qualification.step4.networkAccess')}
              </p>
              <div className="space-y-3">
                {networkOptions.map(access => (
                  <RadioOption
                    key={access.value}
                    name="networkAccess"
                    value={access.value}
                    label={access.label}
                    selected={formData.networkAccess === access.value}
                    onChange={() => updateField('networkAccess', access.value)}
                  />
                ))}
              </div>
            </div>
            
            {/* Sector Experience */}
            <div>
              <p className="text-white font-medium mb-2">
                {t('qualification.step4.sectorExperience')}
              </p>
              <p className="text-[#93a5af] text-sm mb-4">{t('qualification.step4.sectorHint')}</p>
              <TagSelect
                options={sectorOptions}
                selected={formData.sectorExperience}
                onChange={(sectors) => updateField('sectorExperience', sectors)}
              />
            </div>
            
            {/* Contribution */}
            <FormTextarea
              label={t('qualification.step4.contribution')}
              value={formData.contribution}
              onChange={(e) => updateField('contribution', e.target.value)}
              placeholder={t('qualification.step4.contributionPlaceholder')}
            />
            
            {/* Ideal Result */}
            <FormTextarea
              label={t('qualification.step4.idealResult')}
              value={formData.idealResult}
              onChange={(e) => updateField('idealResult', e.target.value)}
              placeholder={t('qualification.step4.idealResultPlaceholder')}
            />
          </div>
        )}
        
        {/* Step 5: Final Assessment */}
        {currentStep === 4 && (
          <div className="space-y-10">
            <div>
              <span className="text-blue-400 text-sm font-medium uppercase tracking-wider">{t('qualification.step5.label')}</span>
              <h2 className="text-2xl md:text-3xl font-bold text-white mt-2">
                {t('qualification.step5.title')}
              </h2>
              <p className="text-[#93a5af] mt-2">{t('qualification.step5.subtitle')}</p>
            </div>
            
            {/* Interest Level */}
            <div>
              <p className="text-white font-medium mb-6">
                {t('qualification.step5.interestLevel')}
              </p>
              <ScaleSelector
                value={formData.interestLevel}
                onChange={(val) => updateField('interestLevel', val)}
                leftLabel={t('qualification.step5.scaleLow')}
                rightLabel={t('qualification.step5.scaleHigh')}
              />
            </div>
            
            {/* Probability to Advance */}
            <div>
              <p className="text-white font-medium mb-6">
                {t('qualification.step5.probabilityToAdvance')}
              </p>
              <ScaleSelector
                value={formData.probabilityToAdvance}
                onChange={(val) => updateField('probabilityToAdvance', val)}
                leftLabel={t('qualification.step5.scaleLow')}
                rightLabel={t('qualification.step5.scaleHigh')}
              />
            </div>
            
            {/* Restrictions */}
            <FormTextarea
              label={t('qualification.step5.restrictions')}
              value={formData.restrictions}
              onChange={(e) => updateField('restrictions', e.target.value)}
              placeholder={t('qualification.step5.restrictionsPlaceholder')}
            />
            
            {/* Confirmation Checkbox */}
            <div className="bg-[#1c262d] border border-white/10 rounded-xl p-6">
              <label className="flex items-start space-x-4 cursor-pointer">
                <div className="relative mt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={formData.confirmData}
                    onChange={(e) => updateField('confirmData', e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                    formData.confirmData ? 'border-blue-500 bg-blue-500' : 'border-[#93a5af]/50'
                  }`}>
                    {formData.confirmData && <Check className="w-4 h-4 text-white" />}
                  </div>
                </div>
                <span className="text-[#93a5af] text-sm leading-relaxed">
                  {t('qualification.step5.confirmation')}
                </span>
              </label>
            </div>
            
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}
          </div>
        )}
        
        {/* Navigation */}
        <div className="flex items-center justify-between mt-12 pt-8 border-t border-white/10">
          {currentStep > 0 ? (
            <button
              onClick={handleBack}
              className="inline-flex items-center text-[#93a5af] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('qualification.navigation.back')}
            </button>
          ) : (
            <div />
          )}
          
          {currentStep < 4 ? (
            <button
              onClick={handleNext}
              data-testid="continue-btn"
              className="inline-flex items-center px-6 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-all"
            >
              {t('qualification.navigation.continue')}
              <ChevronRight className="ml-2 w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              data-testid="submit-btn"
              className="inline-flex items-center px-6 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('qualification.navigation.submitting')}
                </>
              ) : (
                <>
                  {t('qualification.navigation.submit')}
                  <Check className="ml-2 w-5 h-5" />
                </>
              )}
            </button>
          )}
        </div>
      </main>
      
      {/* Footer */}
      <footer className="py-8 text-center border-t border-white/5">
        <p className="text-[#93a5af]/50 text-sm">
          {t('qualification.footer')}
        </p>
      </footer>
    </div>
  );
}
